import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { blogService, api } from '../services';

const resolveImageUrl = (path) => {
  if (!path) {
    return '';
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const baseApiUrl = api?.defaults?.baseURL || 'http://localhost:5000/api';
  const assetBase = baseApiUrl.replace(/\/api\/?$/, '');

  return `${assetBase}/${path}`;
};

const Blogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishedFilter, setPublishedFilter] = useState('all');

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};

      if (publishedFilter !== 'all') {
        params.published = publishedFilter === 'published';
      }

      const response = await blogService.getBlogs(params);
      setBlogs(response.data ?? []);
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
      toast.error(error.response?.data?.message || 'Failed to load blogs');
    } finally {
      setLoading(false);
    }
  }, [publishedFilter]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const handleTogglePublish = async (blog) => {
    try {
      await blogService.updateBlog(blog._id, {
        isPublished: !blog.isPublished,
      });

      toast.success(
        `Blog ${!blog.isPublished ? 'published' : 'unpublished'} successfully`
      );
      fetchBlogs();
    } catch (error) {
      console.error('Failed to toggle publish state:', error);
      toast.error(error.response?.data?.message || 'Failed to update blog');
    }
  };

  const handleDelete = async (blog) => {
    if (!window.confirm(`Delete "${blog.title}"? This cannot be undone.`)) {
      return;
    }

    try {
      await blogService.deleteBlog(blog._id);
      toast.success('Blog deleted successfully');
      fetchBlogs();
    } catch (error) {
      console.error('Failed to delete blog:', error);
      toast.error(error.response?.data?.message || 'Failed to delete blog');
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Blog Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/blogs/new')}
        >
          Create Blog
        </Button>
      </Box>

      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Typography variant="subtitle1">Filter:</Typography>
        <Select
          value={publishedFilter}
          onChange={(event) => setPublishedFilter(event.target.value)}
          size="small"
        >
          <MenuItem value="all">All blogs</MenuItem>
          <MenuItem value="published">Published</MenuItem>
          <MenuItem value="draft">Drafts</MenuItem>
        </Select>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cover</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Chips</TableCell>
              <TableCell>Published</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : blogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No blogs found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              blogs.map((blog) => (
                <TableRow key={blog._id} hover>
                  <TableCell>
                    {blog.coverImage ? (
                      <Box
                        component="img"
                        src={resolveImageUrl(blog.coverImage)}
                        alt={blog.title}
                        sx={{
                          width: 64,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: 1,
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        No image
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle1">{blog.title}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {new Date(blog.createdAt).toLocaleDateString("en-IN", {
                        timeZone: "Asia/Kolkata"
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>{blog.slug}</TableCell>
                  <TableCell>
                    {blog.chips && blog.chips.length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {blog.chips.map((chip) => (
                          <Chip key={chip} label={chip} size="small" />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        No chips
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={Boolean(blog.isPublished)}
                      onChange={() => handleTogglePublish(blog)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => navigate(`/blogs/edit/${blog._id}`)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(blog)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Blogs;

